/**
 * DNCRegistryCard — Card for a national DNC registry configuration
 *
 * Displays registry details: name, country, scrub cadence, channel scope,
 * active toggle, and edit/delete actions.
 */
import { Card, CardContent, Badge, Button } from '@respectly/ui';
import { Switch } from '@respectly/ui';
import { Pencil, Trash2 } from 'lucide-react';
import type { DNCRegistry } from '@respectly/shared-types';

// ============================================
// Types
// ============================================

interface DNCRegistryCardProps {
  registry: DNCRegistry;
  onEdit: (registry: DNCRegistry) => void;
  onDelete?: (registry: DNCRegistry) => void;
  onToggle?: (registry: DNCRegistry) => void;
}

// ============================================
// Component
// ============================================

export function DNCRegistryCard({
  registry,
  onEdit,
  onDelete,
  onToggle,
}: DNCRegistryCardProps) {
  return (
    <Card className="border-slate-200/60 dark:border-slate-700/60">
      <CardContent className="p-4 space-y-3">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-medium text-sm text-slate-900 dark:text-white">
              {registry.name}
            </span>
            <Badge variant="outline" className="text-[10px]">
              {registry.country}
            </Badge>
            <Badge
              variant={registry.isActive ? 'success' : 'secondary'}
              className="text-[10px]"
            >
              {registry.isActive ? 'Active' : 'Inactive'}
            </Badge>
          </div>
          <Switch
            checked={registry.isActive}
            onCheckedChange={() => onToggle?.(registry)}
            className="scale-75"
          />
        </div>

        {/* Registry ID */}
        <div>
          <Badge variant="outline" className="font-mono text-[10px]">
            {registry.registryId}
          </Badge>
        </div>

        {/* Description */}
        {registry.description && (
          <p className="text-xs text-slate-500 dark:text-slate-400 leading-relaxed">
            {registry.description}
          </p>
        )}

        {/* Details */}
        <div className="flex items-center gap-3 flex-wrap">
          <span className="text-xs text-slate-600 dark:text-slate-300">
            Scrub every <span className="font-medium">{registry.scrubCadenceDays}</span> days
          </span>
          {registry.channel ? (
            <Badge variant="secondary" className="text-[10px]">
              {registry.channel}
            </Badge>
          ) : (
            <Badge variant="secondary" className="text-[10px]">
              All Channels
            </Badge>
          )}
          <Badge variant="secondary" className="text-[10px]">
            {registry.registryType}
          </Badge>
        </div>

        {/* Last scrub */}
        <div className="text-xs text-slate-500 dark:text-slate-400">
          Last scrub: <span className="font-medium">Never</span>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 pt-2 border-t border-slate-100 dark:border-slate-800">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onEdit(registry)}
            className="h-7 text-xs"
          >
            <Pencil className="w-3 h-3 mr-1" />
            Edit
          </Button>
          {onDelete && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onDelete(registry)}
              className="h-7 text-xs text-red-600 hover:text-red-700"
            >
              <Trash2 className="w-3 h-3 mr-1" />
              Delete
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
